import type { ApolloClient } from '@apollo/client';
import { Platform } from 'react-native';
import { isNil } from 'es-toolkit';
import { jwtDecode } from 'jwt-decode';

import type { JWTContextUser } from '@savvy/deprecated-shared/api/user-api';
import type { LoginData } from '@savvy/deprecated-shared/utils/savvy-auth';
import { apiHost } from '@savvy/deprecated-shared/api/api-host';
import { fetchCurrentUser, fetchCurrentUserWithoutCaching } from '@savvy/deprecated-shared/api/user-api';
import { LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';
import { AuthFactor } from '@savvy/deprecated-shared/utils/savvy-auth';

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again later.';
const RATE_LIMIT_LOGIN_ERROR_MESSAGE = 'Too many failed log in attempts. Please try again later.';

const users: JWTContextUser[] = [];

function getJwtFromResponse(response: Response): string {
  const authorization = response.headers.get('Authorization');
  if (!authorization) {
    return '';
  }
  const token = authorization.replace('Bearer ', '');
  return token;
}

async function authFetch(url: string, data: LoginData): Promise<Response> {
  return await fetch(url, {
    method: 'POST',
    headers: {
      'Access-Control-Expose-Headers': 'Authorization',
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...data.payload }),
  }).catch((error: unknown) => {
    switch (data.type) {
      case AuthFactor.First:
        console.error(`JWT / Auth Error - [Auth Api] with email: [${data.payload.email}] `, error);
        break;
      case AuthFactor.Second:
        console.error(`JWT / Auth Error - [Auth Api] with otp attempt: [${data.payload.otp_attempt}] `, error);
        break;
    }
    throw new Error(GENERIC_ERROR_MESSAGE);
  });
}

async function getCurrentUser(client: ApolloClient<object>, jwt: string) {
  const { sub } = jwtDecode(jwt);
  const existingUser = users.find((user) => user?.id === sub);
  if (existingUser) {
    return existingUser;
  }

  let user = null;
  if (Platform.OS === 'web') {
    user = await fetchCurrentUser(client);
  } else {
    user = await fetchCurrentUserWithoutCaching();
  }
  if (isNil(user)) {
    return;
  }
  users.push(user);
  return user;
}

async function handleErrorResponse(response: Response, fallbackMessage?: string): Promise<string | undefined> {
  if (response.status === 429) {
    return RATE_LIMIT_LOGIN_ERROR_MESSAGE;
  }
  if (response.ok) {
    return;
  }
  if (response.status >= 400 && response.status <= 499) {
    type JsonReponseType = {
      status?: { message?: string };
      error?: string;
    };
    // eslint-disable-next-line typescript/consistent-type-assertions -- FIXME: This rule was recently enabled for the repo
    const jsonResponse = (await response.json()) as JsonReponseType;
    return jsonResponse.status?.message || jsonResponse.error || fallbackMessage || GENERIC_ERROR_MESSAGE;
  }
  return GENERIC_ERROR_MESSAGE;
}

type LoginResponse = {
  err?: string;
  jwt: string;
};

export class AuthApi {
  client: ApolloClient<object>;
  constructor(client: ApolloClient<object>) {
    this.client = client;
  }
  // eslint-disable-next-line class-methods-use-this -- Unsure which syntax we should use
  async login(data: LoginData): Promise<LoginResponse> {
    const response = await authFetch(`${apiHost}/login`, data);
    const err = await handleErrorResponse(response, 'Please check your email and password.');
    const jwt = getJwtFromResponse(response);
    await LocalStorage.setItem('accessToken', jwt);
    return { err, jwt };
  }

  async me(jwt: string) {
    try {
      const user = await getCurrentUser(this.client, jwt);

      if (!user) {
        throw new Error('Invalid authorization token. You may need to clear your cache.');
      }
      return user;
    } catch (err) {
      console.error('JWT / Auth Error - failed to get current user: ', err);
      throw new Error('Internal server error');
    }
  }

  // eslint-disable-next-line class-methods-use-this -- FIXME: This rule was recently enabled for the repo
  async logout() {
    const jwt = await LocalStorage.getItem('accessToken');
    await LocalStorage.removeItem('accessToken');
    return await fetch(`${apiHost}/logout`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  // eslint-disable-next-line class-methods-use-this -- Unsure which syntax we should use
  reset(): void {
    users.splice(0, users.length);
  }
}
